import type {
  PosSalesByPaymentMethodQueryDTO,
  SalesByPaymentMethodItemDTO,
} from "../../domain/contracts/reports";
import type { Shift } from "../../domain/repo/get-shifts";
import { formatDateLocal, formatDateForInput, formatTime24 } from "@/shared/lib/date-time";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { useRouterBack } from "@/shared/hooks/useRouterBack";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Label,
  Loader,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@devhop/ui";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  useLatestShift,
  useOpenShift,
  usePaymentMethods,
  useSalesByPaymentMethodReport,
} from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import { OFFLINE_SYNC_CHANGED_EVENT } from "../lib/offline-sync/db";
import {
  defaultPosReportDateRangeIso,
  hasReportShiftFilter,
  monthRangeIsoFromInputMonth,
  monthValueFromDateFromIso,
} from "../lib/report-date-range";

const PAGE_SIZE = 20;

function formatNum(n: number): string {
  return n.toLocaleString("lo-LA");
}

function formatShiftLabel(shift: Shift): string {
  const dateStr = formatDateLocal(shift.openedAt);
  const timeStr = formatTime24(shift.openedAt);
  const statusStr = shift.status === "OPEN" ? " (ເປີດ)" : " (ປິດ)";
  return `${dateStr} ${timeStr}${statusStr}`;
}

function rowLabel(
  row: SalesByPaymentMethodItemDTO,
  groupBy: PosSalesByPaymentMethodQueryDTO["groupBy"],
): string {
  if (groupBy === "method") return row.methodName || "—";
  if (groupBy === "type") return row.methodType ?? "—";
  if (groupBy === "date") return row.date ?? "—";
  if (groupBy === "branch") return row.branchName ?? "—";
  return "—";
}

export function PaymentMethodsReportPage() {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const deviceId = selection?.deviceId ?? null;
  const goBack = useRouterBack();

  const defaultRange = useMemo(() => defaultPosReportDateRangeIso(), []);

  const { data: openShift } = useOpenShift(branchId, deviceId);
  const { data: latestShift } = useLatestShift(branchId);
  /** null = ໃຊ້ກະປັจຈຸບັນ, "" = ກອງຕາມເດືອນ (ບໍ່ລະບຸກະ) */
  const [shiftOverride, setShiftOverride] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [methodIds, setMethodIds] = useState("__all__");
  const [groupBy, setGroupBy] =
    useState<PosSalesByPaymentMethodQueryDTO["groupBy"]>("method");
  const [page, setPage] = useState(1);

  const currentShiftId = openShift?.id ?? latestShift?.id ?? "";
  const effectiveShiftIds =
    shiftOverride === "" ? "" : (shiftOverride ?? currentShiftId);

  useEffect(() => {
    setShiftOverride(null);
    setPage(1);
  }, [branchId, deviceId]);

  const shiftMode = hasReportShiftFilter(effectiveShiftIds);
  const monthInput = shiftMode ? "" : monthValueFromDateFromIso(dateFrom);
  const comboboxShiftValue = shiftMode ? effectiveShiftIds : "";

  const reportQuery = useMemo(() => {
    if (!branchId) return null;
    const base: Omit<PosSalesByPaymentMethodQueryDTO, "branchId"> = {
      groupBy,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      status: "CAPTURED",
      methodIds:
        methodIds && methodIds !== "__all__" ? methodIds : undefined,
    };
    if (shiftMode) {
      return {
        ...base,
        shiftIds: effectiveShiftIds,
        dateFrom: undefined,
        dateTo: undefined,
      };
    }
    return { ...base, dateFrom, dateTo, shiftIds: undefined };
  }, [
    branchId,
    groupBy,
    page,
    methodIds,
    shiftMode,
    effectiveShiftIds,
    dateFrom,
    dateTo,
  ]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useSalesByPaymentMethodReport(branchId, reportQuery);
  const { data: paymentMethods = [] } = usePaymentMethods(branchId);

  useEffect(() => {
    const refreshAfterSync = () => {
      void refetch();
    };
    window.addEventListener(OFFLINE_SYNC_CHANGED_EVENT, refreshAfterSync);
    return () => {
      window.removeEventListener(OFFLINE_SYNC_CHANGED_EVENT, refreshAfterSync);
    };
  }, [refetch]);

  const shiftsLookupUrl = branchId
    ? `${config.apiUrl}/pos/shifts/lookup?branchId=${branchId}`
    : "";

  const fetchShifts = async ({
    search,
    pageParam,
  }: {
    search: string;
    pageParam: number;
  }) => {
    if (!branchId) return { items: [], nextPage: null };
    const limit = 20;
    const pageNum =
      typeof pageParam === "number" && pageParam > 0 ? pageParam : 1;
    const skip = (pageNum - 1) * limit;
    const url = new URL(shiftsLookupUrl);
    if (search.trim()) url.searchParams.set("q", search);
    url.searchParams.set("limit", String(limit));
    url.searchParams.set("skip", String(skip));
    const res = await fetcher.get<{ items: Shift[]; total?: number }>(
      url.toString(),
    );
    const items = Array.isArray(res.items) ? res.items : [];
    const total = typeof res.total === "number" ? res.total : items.length;
    return {
      items: items.map((s) => ({ ...s, name: formatShiftLabel(s) })),
      nextPage: skip + limit < total ? pageNum + 1 : null,
    };
  };

  const preloadShift = async (id: string) => {
    if (!branchId || !id) return null;
    const url = `${config.apiUrl}/pos/shifts/lookup/${encodeURIComponent(id)}?branchId=${branchId}`;
    const res = await fetcher.get<{ item?: Shift | null }>(url);
    const shift = res.item;
    if (!shift) return null;
    return { ...shift, name: formatShiftLabel(shift) };
  };

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const summary = data?.summary;

  const applyMonth = (yyyyMm: string) => {
    const range = monthRangeIsoFromInputMonth(yyyyMm);
    if (!range) return;
    setShiftOverride("");
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setPage(1);
  };

  const handleShiftChange = (value: string) => {
    const id = value.trim();
    if (!id) {
      setShiftOverride("");
      setPage(1);
      return;
    }
    if (id === currentShiftId) {
      setShiftOverride(null);
    } else {
      setShiftOverride(id);
    }
    setPage(1);
  };

  const clearFilters = () => {
    const r = defaultPosReportDateRangeIso();
    setShiftOverride(null);
    setDateFrom(r.dateFrom);
    setDateTo(r.dateTo);
    setMethodIds("__all__");
    setGroupBy("method");
    setPage(1);
  };

  if (!branchId) {
    return (
      <div className="container mx-auto max-w-5xl p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BarChart3 className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີສາຂາ</EmptyTitle>
            <EmptyDescription>ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-h-[calc(100svh-10rem)] max-w-5xl overflow-y-auto p-4">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="font-bold text-2xl">ລາຍງານການຊຳລະຕາມວິທີ</h1>
          <p className="text-muted-foreground text-sm">
            ສະຫຼຸບຍອດຂາຍ ຄ່າທຳນຽມ ແລະ ລາຍໄດ້ສຸດທິຕາມວິທີຊຳລະ — ຈາກ server
            (order ທີ່ sync ແລ້ວເທົ່ານັ້ນ)
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1 space-y-1">
            <Label className="text-muted-foreground text-xs">ກະ</Label>
            <InfiniteCombobox<Shift & { name: string }>
              queryKey={["pos", "report", "shifts", branchId]}
              queryFn={fetchShifts}
              preloadQueryFn={preloadShift}
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              value={comboboxShiftValue}
              onValueChange={handleShiftChange}
              placeholder="ທຸກກະ (ລ້າງເພື່ອກອງຕາມເດືອນ)"
              className="h-9 w-full"
              clearable
            />
          </div>

          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">ເດືອນ</Label>
            <input
              type="month"
              className="flex h-9 rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50"
              value={monthInput}
              disabled={shiftMode}
              onChange={(e) => applyMonth(e.target.value)}
            />
          </div>

          <div className="min-w-[160px] space-y-1">
            <Label className="text-muted-foreground text-xs">ວິທີຊຳລະ</Label>
            <SimpleSelect
              value={methodIds}
              onValueChange={(val) => {
                setMethodIds(val);
                setPage(1);
              }}
              placeholder="ທຸກວິທີ"
              options={[
                { value: "__all__", label: "ທຸກວິທີ" },
                ...paymentMethods.map((m) => ({
                  value: m.id,
                  label: m.displayName,
                })),
              ]}
              className="h-9 w-full min-w-[160px]"
            />
          </div>

          <div className="min-w-[140px] space-y-1">
            <Label className="text-muted-foreground text-xs">ຈັດກຸ່ມ</Label>
            <SimpleSelect
              value={groupBy}
              onValueChange={(val) => {
                setGroupBy(
                  val as PosSalesByPaymentMethodQueryDTO["groupBy"],
                );
                setPage(1);
              }}
              options={[
                { value: "method", label: "ວິທີຊຳລະ" },
                { value: "type", label: "ປະເພດ" },
                { value: "date", label: "ວັນທີ" },
              ]}
              className="h-9 w-full"
            />
          </div>

          <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
            ລ້າງຕົວກອງ
          </Button>
        </div>

        {!shiftMode && (
          <div className="flex flex-wrap gap-3 text-muted-foreground text-xs">
            <span>
              ຊ່ວງ: {formatDateForInput(dateFrom)} – {formatDateForInput(dateTo)}
            </span>
          </div>
        )}
      </div>

      {summary && (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border bg-card p-3">
            <p className="text-muted-foreground text-xs">ລາຍໄດ້ລວມ (Gross)</p>
            <p className="font-bold text-xl">{formatNum(summary.totalGross)} ₭</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <p className="text-muted-foreground text-xs">ຄ່າທຳນຽມ</p>
            <p className="font-bold text-xl">{formatNum(summary.totalFees)} ₭</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <p className="text-muted-foreground text-xs">ລາຍໄດ້ສຸດທິ (Net)</p>
            <p className="font-bold text-xl">{formatNum(summary.totalNet)} ₭</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <p className="text-muted-foreground text-xs">ຈຳນວນການຊຳລະ</p>
            <p className="font-bold text-xl">
              {formatNum(summary.totalTransactions)}
            </p>
          </div>
        </div>
      )}

      {error instanceof Error && (
        <p className="mb-4 text-destructive text-sm">{error.message}</p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <span className="inline-flex size-8 animate-spin text-muted-foreground">
            <Loader />
          </span>
        </div>
      ) : rows.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BarChart3 className="h-12 w-12 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີຂໍ້ມູນ</EmptyTitle>
            <EmptyDescription>
              ບໍ່ພົບການຊຳລະໃນຊ່ວງຫຼືຕົວກອງທີ່ເລືອກ
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          {isFetching && !isLoading && (
            <p className="mb-2 text-muted-foreground text-xs">ກຳລັງອັບເດດ...</p>
          )}
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="px-3 py-2 font-medium">
                    {groupBy === "method"
                      ? "ວິທີຊຳລະ"
                      : groupBy === "type"
                        ? "ປະເພດ"
                        : groupBy === "date"
                          ? "ວັນທີ"
                          : "ລາຍການ"}
                  </th>
                  {groupBy === "method" && (
                    <th className="px-3 py-2 font-medium">ປະເພດ</th>
                  )}
                  <th className="px-3 py-2 text-right font-medium">ຈຳນວນ</th>
                  <th className="px-3 py-2 text-right font-medium">Gross</th>
                  <th className="px-3 py-2 text-right font-medium">ຄ່າທຳນຽມ</th>
                  <th className="px-3 py-2 text-right font-medium">Net</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={`${rowLabel(row, groupBy)}-${i}`} className="border-b">
                    <td className="px-3 py-2 font-medium">
                      {rowLabel(row, groupBy)}
                    </td>
                    {groupBy === "method" && (
                      <td className="px-3 py-2 text-muted-foreground">
                        {row.methodType ?? "—"}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right">
                      {formatNum(row.transactionCount)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatNum(row.grossAmount)} ₭
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatNum(row.feeAmount)} ₭
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatNum(row.netAmount)} ₭
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <span
                      className={
                        page === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    >
                      <PaginationPrevious
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      />
                    </span>
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNum) => (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          isActive={pageNum === page}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}
                  <PaginationItem>
                    <span
                      className={
                        page >= totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    >
                      <PaginationNext
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                      />
                    </span>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
}
